import React, { useEffect, useState } from 'react';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
import { StatCard } from '../../components/shared/StatCard';
import { PaymentCard } from '../../components/tenant/PaymentCard';
import { ActivityTimeline } from '../../components/shared/ActivityTimeline';
import { QuickActions } from '../../components/shared/QuickActions';
import { Breadcrumbs } from '../../components/shared/Breadcrumbs';
import { CashflowPredictor } from '../../components/ai/CashflowPredictor';
import { PredictiveInsights } from '../../components/ai/PredictiveInsights';
import { AIAssistant } from '../../components/ai/AIAssistant';
import { Skeleton, CardSkeleton } from '../../components/shared/Skeleton';
import { useAuth } from '../../hooks/useAuth';
import { MOCK_TENANT } from '../../utils/mockData';
import { Calendar, ShieldCheck, Clock, FileText, Award, TrendingUp, Wrench, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { useToast } from '../../hooks/useToast';
import { useNavigate } from 'react-router-dom';
export function TenantDashboard() {
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const tenant = MOCK_TENANT;
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  const handlePay = () => {
    toast.success('Payment processed successfully!');
  };
  // AI Insights for tenant
  const tenantInsights = [{
    type: 'success' as const,
    title: 'Payment on Track',
    description: "You're maintaining an excellent payment record. Keep it up to improve your rent credit score!",
    confidence: 95,
    action: 'View Credit Score',
    onClick: () => navigate('/tenant/rent-history')
  }, {
    type: 'info' as const,
    title: 'Cashflow Optimization',
    description: 'Based on your salary cycle, we recommend paying on the 28th to avoid cashflow stress.',
    confidence: 92,
    action: 'Adjust Payment Date',
    onClick: () => navigate('/tenant/payment-flexibility')
  }, {
    type: 'tip' as const,
    title: 'Save on Fees',
    description: 'Enable auto-debit to avoid late payment fees and build your reliability score faster.',
    confidence: 88,
    action: 'Enable Auto-Debit',
    onClick: () => navigate('/tenant/settings')
  }];
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 pb-16 lg:pb-0 transition-colors" data-id="element-5043">
      <Sidebar data-id="element-5044" />
      <AIAssistant data-id="element-5045" />

      <main className="flex-1 lg:ml-[240px] transition-all duration-300 w-full" data-id="element-5046">
        <DashboardHeader data-id="element-5047" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-id="element-5048">
          <Breadcrumbs data-id="element-5049" />

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-5050">
            <div className="mb-8" data-id="element-5051">
              {isLoading ? <div className="space-y-2" data-id="element-5052">
                  <Skeleton variant="text" width={250} height={32} data-id="element-5053" />
                  <Skeleton variant="text" width={300} height={20} data-id="element-5054" />
                </div> : <>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5055">
                    Welcome back, {user?.name.split(' ')[0]}
                  </h1>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-5056">
                    Here's what's happening with your rent today.
                  </p>
                </>}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-5057">
              {/* Left Column - Main Info */}
              <div className="lg:col-span-2 space-y-6 lg:space-y-8" data-id="element-5058">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5059">
                  {isLoading ? <>
                      <CardSkeleton data-id="element-5060" />
                      <CardSkeleton data-id="element-5061" />
                    </> : <>
                      <StatCard title="Coverage Status" value="Active" icon={ShieldCheck} color="green" data-id="element-5062" />
                      <StatCard title="Reliability Score" value={tenant.reliabilityScore?.score || 85} icon={Award} color="blue" data-id="element-5063" />
                    </>}
                </div>

                {/* AI Insights */}
                {!isLoading && <PredictiveInsights insights={tenantInsights} userType="tenant" data-id="element-5064" />}

                {/* Cashflow Predictor */}
                {!isLoading && <CashflowPredictor data-id="element-5065" />}

                {/* Feature Highlights */}
                {!isLoading && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5066">
                    <motion.button onClick={() => navigate('/tenant/certificate')} whileHover={{
                  y: -2
                }} className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white text-left shadow-lg hover:shadow-xl transition-all" data-id="element-5067">
                      <div className="flex items-start justify-between mb-4" data-id="element-5068">
                        <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm" data-id="element-5069">
                          <Award className="h-6 w-6" data-id="element-5070" />
                        </div>
                        <span className="text-xs bg-white/20 px-2 py-1 rounded-full backdrop-blur-sm" data-id="element-5071">
                          New
                        </span>
                      </div>
                      <h3 className="text-lg font-bold mb-1" data-id="element-5072">
                        Rent Certificate
                      </h3>
                      <p className="text-sm text-blue-100" data-id="element-5073">
                        Download your verified rent guarantee certificate
                      </p>
                    </motion.button>

                    <motion.button onClick={() => navigate('/tenant/rent-history')} whileHover={{
                  y: -2
                }} className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white text-left shadow-lg hover:shadow-xl transition-all" data-id="element-5074">
                      <div className="flex items-start justify-between mb-4" data-id="element-5075">
                        <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm" data-id="element-5076">
                          <TrendingUp className="h-6 w-6" data-id="element-5077" />
                        </div>
                        <span className="text-xs bg-white/20 px-2 py-1 rounded-full backdrop-blur-sm" data-id="element-5078">
                          New
                        </span>
                      </div>
                      <h3 className="text-lg font-bold mb-1" data-id="element-5079">Rent History</h3>
                      <p className="text-sm text-green-100" data-id="element-5080">
                        View your payment track record & score
                      </p>
                    </motion.button>

                    <motion.button onClick={() => navigate('/tenant/payment-flexibility')} whileHover={{
                  y: -2
                }} className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white text-left shadow-lg hover:shadow-xl transition-all" data-id="element-5081">
                      <div className="flex items-start justify-between mb-4" data-id="element-5082">
                        <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm" data-id="element-5083">
                          <Zap className="h-6 w-6" data-id="element-5084" />
                        </div>
                        <span className="text-xs bg-white/20 px-2 py-1 rounded-full backdrop-blur-sm" data-id="element-5085">
                          New
                        </span>
                      </div>
                      <h3 className="text-lg font-bold mb-1" data-id="element-5086">
                        Payment Flexibility
                      </h3>
                      <p className="text-sm text-purple-100" data-id="element-5087">
                        Adjust payment dates & request emergency skip
                      </p>
                    </motion.button>

                    <motion.button onClick={() => navigate('/tenant/maintenance')} whileHover={{
                  y: -2
                }} className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white text-left shadow-lg hover:shadow-xl transition-all" data-id="element-5088">
                      <div className="flex items-start justify-between mb-4" data-id="element-5089">
                        <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm" data-id="element-5090">
                          <Wrench className="h-6 w-6" data-id="element-5091" />
                        </div>
                        <span className="text-xs bg-white/20 px-2 py-1 rounded-full backdrop-blur-sm" data-id="element-5092">
                          New
                        </span>
                      </div>
                      <h3 className="text-lg font-bold mb-1" data-id="element-5093">Maintenance</h3>
                      <p className="text-sm text-orange-100" data-id="element-5094">
                        Submit & track property maintenance requests
                      </p>
                    </motion.button>
                  </div>}

                {/* Next Payment */}
                <section data-id="element-5095">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-5096">
                    Upcoming Payment
                  </h2>
                  {isLoading ? <CardSkeleton data-id="element-5097" /> : <PaymentCard amount={tenant.rentAmount} dueDate={tenant.nextPaymentDate} status="pending" onPay={handlePay} data-id="element-5098" />}
                </section>

                {/* Activity Timeline */}
                {isLoading ? <CardSkeleton data-id="element-5099" /> : <ActivityTimeline data-id="element-5100" />}
              </div>

              {/* Right Column - Quick Actions & Support */}
              <div className="space-y-6 lg:space-y-8" data-id="element-5101">
                {isLoading ? <CardSkeleton data-id="element-5102" /> : <QuickActions data-id="element-5103" />}

                {isLoading ? <CardSkeleton data-id="element-5104" /> : <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors" data-id="element-5105">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-5106">
                      Quick Links
                    </h3>
                    <div className="space-y-3" data-id="element-5107">
                      <button onClick={() => navigate('/tenant/documents')} className="w-full text-left px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 group" data-id="element-5108">
                        <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-5109" />
                        Download Rent Receipt
                      </button>
                      <button onClick={() => navigate('/tenant/certificate')} className="w-full text-left px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 group" data-id="element-5110">
                        <ShieldCheck className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-5111" />
                        View Rent Certificate
                      </button>
                      <button onClick={() => navigate('/tenant/payment-flexibility')} className="w-full text-left px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 group" data-id="element-5112">
                        <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-5113" />
                        Change Payment Date
                      </button>
                    </div>
                  </div>}

                {isLoading ? <CardSkeleton data-id="element-5114" /> : <div className="bg-blue-700 dark:bg-blue-800 rounded-xl shadow-sm p-6 text-white relative overflow-hidden transition-colors" data-id="element-5115">
                    <div className="relative z-10" data-id="element-5116">
                      <h3 className="text-lg font-medium mb-2" data-id="element-5117">Need Help?</h3>
                      <p className="text-blue-100 dark:text-blue-200 text-sm mb-4" data-id="element-5118">
                        Our support team is available 24/7 to assist with your
                        rent issues.
                      </p>
                      <button onClick={() => navigate('/help')} className="w-full py-2 bg-white text-blue-700 dark:text-blue-800 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors shadow-sm" data-id="element-5119">
                        Contact Support
                      </button>
                    </div>
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-blue-600 dark:bg-blue-700 rounded-full opacity-50 blur-xl" data-id="element-5120"></div>
                  </div>}
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>;
}